import { CurrencyPipe, DecimalPipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { GovcoAlertModalComponent } from "../../../../shared/govco/govco-alert-modal/govco-alert-modal.component";
import { GovcoConfirmModalComponent } from "../../../../shared/govco/govco-confirm-modal/govco-confirm-modal.component";
import {
  BankProject,
  ProjectBankService,
} from "../../../../core/services/project-bank.service";

type BankView = "territory" | "featured" | "compare";

@Component({
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    CurrencyPipe,
    DecimalPipe,
    GovcoAlertModalComponent,
    GovcoConfirmModalComponent,
  ],
  templateUrl: './project-bank.component.html',
  styleUrl: './project-bank.component.css',
})
export class ProjectBankComponent {
  readonly bank = inject(ProjectBankService);
  view: BankView = "territory";
  query = "";
  typeFilter = "";
  authorityFilter = "";
  departmentFilter = "";
  municipalityFilter = "";
  investmentRange = 5000;
  ecosystemFilters: string[] = [];
  typeFilters: string[] = [];
  statusFilters: string[] = [];
  featuredOrder = "impact";
  selectedIds: string[] = [];
  selected?: BankProject;
  message = "";
  readonly ecosystems = ["Bosques andinos", "Páramos", "Bosques secos", "Humedales", "Manglares", "Otros"];
  readonly projectTypes = ["PSA", "ICADP", "PSA e ICADP"];
  readonly projectStatuses = ["Abierto", "En estructuración", "En implementación"];
  readonly municipalities = ["Valledupar", "Cúcuta", "Ibagué"];
  get departments(): string[] { return [...new Set(this.bank.available().map((item) => item.department))]; }
  get authorities(): string[] {
    return [...new Set(this.bank.available().map((item) => item.authority))];
  }
  get filtered(): BankProject[] {
    const q = this.query.trim().toLowerCase();
    return this.bank
      .available()
      .filter(
        (item) =>
          (!q ||
            Object.values(item).some((value) =>
              String(value).toLowerCase().includes(q),
            )) &&
          (!this.departmentFilter || item.department === this.departmentFilter) &&
          (!this.municipalityFilter || this.municipalityFor(item) === this.municipalityFilter) &&
          (!this.typeFilter || item.type === this.typeFilter) &&
          (!this.authorityFilter || item.authority === this.authorityFilter) &&
          (!this.ecosystemFilters.length || this.ecosystemFilters.some(filter => this.matchesEcosystem(item.ecosystem, filter))) &&
          (!this.typeFilters.length || this.typeFilters.includes(item.type) || this.typeFilters.includes("PSA e ICADP")) &&
          item.totalAmount <= this.investmentRange * 1000000,
      );
  }
  get featured(): BankProject[] {
    return [...this.bank.available()].sort((a, b) =>
      this.featuredOrder === "investment"
        ? b.totalAmount - a.totalAmount
        : this.featuredOrder === "recent"
          ? b.validatedAt.localeCompare(a.validatedAt)
          : b.conservationHectares - a.conservationHectares,
    );
  }
  get comparison(): BankProject[] {
    return this.selectedIds
      .map((id) => this.bank.find(id))
      .filter((project): project is BankProject => !!project);
  }
  readonly comparisonRows = [
    {
      label: "Familias potenciales",
      value: (p: BankProject) => String(p.potentialFamilies),
    },
    {
      label: "Predios potenciales",
      value: (p: BankProject) =>
        p.potentialProperties ? String(p.potentialProperties) : "No aplica",
    },
    {
      label: "Hectáreas de conservación",
      value: (p: BankProject) => `${p.conservationHectares} Ha`,
      emphasis: true,
    },
    {
      label: "Inversión total",
      value: (p: BankProject) => `$${(p.totalAmount / 1000000).toFixed(0)} M`,
    },
    { label: "Ecosistema principal", value: (p: BankProject) => p.ecosystem },
    { label: "Autoridad ambiental", value: (p: BankProject) => p.authority },
    { label: "Fecha de validación", value: (p: BankProject) => p.validatedAt },
  ];
  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }
  toggleSelection(id: string): void {
    if (this.isSelected(id)) {
      this.selectedIds = this.selectedIds.filter((selected) => selected !== id);
      return;
    }
    if (this.selectedIds.length < 2)
      this.selectedIds = [...this.selectedIds, id];
    else
      this.message =
        "Puede comparar máximo dos proyectos a la vez. Retire uno de su selección para agregar otro.";
  }
  toggleEcosystem(ecosystem: string): void {
    this.ecosystemFilters = this.toggleValue(this.ecosystemFilters, ecosystem);
  }
  toggleType(type: string): void { this.typeFilters = this.toggleValue(this.typeFilters, type); }
  toggleStatus(status: string): void { this.statusFilters = this.toggleValue(this.statusFilters, status); }
  private toggleValue(values: string[], value: string): string[] { return values.includes(value) ? values.filter(item => item !== value) : [...values, value]; }
  private matchesEcosystem(value: string, filter: string): boolean { return value === filter || (filter === "Bosques secos" && value === "Bosque seco tropical"); }
  municipalityFor(project: BankProject): string { return project.department === "Cesar" ? "Valledupar" : project.department === "Tolima" ? "Ibagué" : "Cúcuta"; }
  clearFilters(): void {
    this.query = "";
    this.typeFilter = "";
    this.authorityFilter = "";
    this.departmentFilter = "";
    this.municipalityFilter = "";
    this.investmentRange = 5000;
    this.ecosystemFilters = [];
    this.typeFilters = [];
    this.statusFilters = [];
  }
  assignSelected(): void {
    if (!this.selected) return;
    const assigned = this.bank.assign(this.selected.id);
    this.selected = undefined;
    this.selectedIds = this.selectedIds.filter((id) => id !== assigned?.id);
    if (assigned)
      this.message = `${assigned.name} fue asignado a ${assigned.implementer}. Se registró la asignación en el log de auditoría y el proyecto salió del Banco.`;
  }
}
